import { DualUploadComponent } from '..';

import { useEffect, useMemo, useRef, useState } from 'react';

import { ArrowDown01Icon, ArrowUp01Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import { isAcceptedUpload } from 'helpers/candidate_application/id-requirements';
import { useSelector } from 'react-redux';

import { DocumentUpload } from 'components/FormComponents/DocumentUpload/DocumentUpload';
import { FormMessage } from 'components/FormComponents/FormMessage/FormMessage';
import { MultipleDocumentUpload } from 'components/FormComponents/MultipleDocumentUpload/MultipleDocumentUpload';
import { IconComponent } from 'components/Icon/IconComponent';
import { StatusPill } from 'components/Pills/StatusPills';

import { selectCandidateApplication } from 'api/candidate-application-slice';

import styles from './IdRequirementsList.module.scss';

export const IdRequirementsList = ({
  code,
  uploadCode,
  brand,
  options = [],
  className,
  wrapperRequirementsMet = false,
  hasIdRequirements = false,
  requirementsMet = false,
  getUpload,
  signUpload,
  createUpload,
  deleteUpload,
  modelParams,
  min = 0,
  title,
  description,
  onPointsChange,
  setValue,
  rejectedData,
}) => {
  const {
    candidateFields,
    application: { id_uploads, other_uploads },
  } = useSelector(selectCandidateApplication);

  // Store the latest callback in a ref to avoid re-render loops
  const onPointsChangeRef = useRef(onPointsChange);
  onPointsChangeRef.current = onPointsChange;

  const [showList, setShowList] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);

  // Calculate current points based on uploaded documents
  const currentPoints = useMemo(() => {
    const points = options.reduce((total, option) => {
      const existing = id_uploads?.find(
        u =>
          u?.metadata?.id_type === option.value &&
          u?.id_upload_detail?.status !== 'rejected' &&
          u?.id_upload_detail?.status !== null &&
          (u?.id_upload_detail?.status !== 'failed' || u?.metadata?.attempts >= 3),
      );
      const earned = existing ? (option.disabled ? 0 : option.points || 0) : 0;
      return total + earned;
    }, 0);
    return points;
  }, [id_uploads, options]);

  const handleVerifyState = state => {
    setIsVerifying(state);
  };

  // Notify parent component when points change
  useEffect(() => {
    if (onPointsChangeRef.current) {
      onPointsChangeRef.current(currentPoints);
    }
  }, [currentPoints]); // Only depend on the actual calculated points value

  // Compute filtered uploads - only include accepted IDs (verified, needs_review, or failed with >= 3 attempts)
  const idsUploadedOnList = useMemo(() => {
    return (
      id_uploads?.filter(upload => {
        // Check if upload matches any option in this list
        const matchesOption = options.some(option => option.value === upload.metadata?.id_type);
        if (!matchesOption) return false;

        // Only include accepted uploads
        return isAcceptedUpload(upload);
      }) || []
    );
  }, [id_uploads, options]);

  const idsUploadedOnListNotDisabled = useMemo(() => {
    return (idsUploadedOnList || [])?.filter(upload =>
      options.some(option => option.value === upload.metadata?.id_type && !option.disabled),
    );
  }, [idsUploadedOnList, options]);

  // Use ref to track previous value and prevent unnecessary updates
  const prevUploadedIdsRef = useRef([]);

  // Save uploaded document values to form
  useEffect(() => {
    if (!setValue) return;

    const currentUploadedIds = idsUploadedOnList
      .map(upload => upload.metadata?.id_type)
      .filter(Boolean);

    // Only update if the values have actually changed
    const hasChanged =
      currentUploadedIds.length !== prevUploadedIdsRef.current.length ||
      currentUploadedIds.some((id, index) => id !== prevUploadedIdsRef.current[index]);

    if (hasChanged) {
      setValue(`${code}`, currentUploadedIds);
      prevUploadedIdsRef.current = currentUploadedIds;
    }
  }, [idsUploadedOnList, code, setValue]);

  const selfieId = useMemo(() => {
    return other_uploads?.find(u => u?.metadata?.selfie)?.id;
  }, [other_uploads]);

  return (
    <div
      className={cn(
        styles.root,
        { [className]: className },
        'd-flex flex-column u-gap-medium justify-content-center align-items-start u-padding',
        options.length > 0 && 'u-border-radius--medium u-border',
        wrapperRequirementsMet &&
          idsUploadedOnList?.length >= 0 &&
          !isVerifying &&
          'u-border-success',
        isVerifying && 'u-border-in-progress',
      )}>
      <div className="d-flex justify-content-between align-items-start u-width-100">
        <div className="d-flex flex-wrap justify-content-between align-items-start u-gap-medium u-width-100">
          <div className="d-flex flex-column u-gap-small u-width-75">
            <span className="primary-text u-font-size-small u-font-weight-500">{title}</span>
            {description && (
              <span className="secondary-text u-font-size-x-small u-font-weight-400">
                {description}
              </span>
            )}
          </div>
          <div className="d-flex align-items-center u-gap">
            {wrapperRequirementsMet && hasIdRequirements && !isVerifying && (
              <StatusPill text="Requirements Met" status="success" />
            )}
            {!hasIdRequirements && <StatusPill text={'Optional'} status={'light'} />}
          </div>
        </div>
        {wrapperRequirementsMet && (
          <div className="d-flex">
            <IconComponent
              icon={showList ? ArrowUp01Icon : ArrowDown01Icon}
              noBackground
              type="secondary"
              size="small"
              strokeWidth={1.5}
              onClick={() => setShowList(!showList)}
            />
          </div>
        )}
      </div>
      {idsUploadedOnListNotDisabled && idsUploadedOnListNotDisabled?.length > 0 && (
        <div className="d-flex flex-column u-gap">
          <div className="d-flex u-gap-small flex-wrap">
            <StatusPill
              text={`${idsUploadedOnListNotDisabled?.length} document(s) uploaded`}
              status="neutral"
            />
          </div>
        </div>
      )}
      {min > 0 && hasIdRequirements && !requirementsMet && (
        <FormMessage
          message={
            options?.length > 1
              ? `You need to upload at least ${min} document(s) from this list`
              : `You need to upload the following document`
          }
          type="Neutral"
          noMargin
          closeable={false}
        />
      )}
      {showList &&
        options.map(option => {
          const existing = id_uploads?.filter(u => u?.metadata?.id_type === option.value) || null;
          const hasBypassedFaceSimilarity = id_uploads?.find(
            u =>
              u?.id_upload_detail?.face_similarity_status === 'failed' &&
              u?.metadata?.attempts >= 3,
          );
          const isFileRejected = existing?.some(file => rejectedData?.files?.includes(file.id));

          // For dual uploads, find front and back uploads
          const frontUpload = existing?.[0] || null;
          const backUploadId = frontUpload?.id_upload_detail?.back_upload_id;
          const backUpload = backUploadId
            ? id_uploads?.find(u => u.id === backUploadId) || null
            : null;
          return !option.allowMultipleUploads ? (
            option.dual ? (
              <DualUploadComponent
                key={option?.value}
                uploadCode={uploadCode}
                selfieId={selfieId}
                brand={brand}
                type={option.idType}
                selected={option.selected}
                verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
                skipVerification={option?.skipVerification || false}
                reTakeSelfie={
                  candidateFields?.hiddenValues?.face_similarity_passed === false &&
                  !hasBypassedFaceSimilarity
                }
                documentTitle={option?.name}
                documentDescription={option?.description}
                additionalInstructions={option?.additionalInstructions}
                disabled={option?.disabled}
                countryCode={option?.countryCode}
                backConditions={option?.backConditions}
                expiryTolerance={option?.expiryTolerance}
                frontValue={frontUpload?.id || null}
                backValue={backUpload?.id || null}
                listCode={code}
                metadata={{
                  ...(option?.metadata || {}),
                  id_type: option?.value,
                  attempts: frontUpload?.metadata?.attempts || 0,
                }}
                modelParams={modelParams}
                uploadStatus={frontUpload?.id_upload_detail?.status}
                uploadError={frontUpload?.id_upload_detail?.reason_for_failure}
                displayFields={option?.inputFields}
                idValues={(() => {
                  const extracted = frontUpload?.id_upload_detail?.extracted_fields;
                  return extracted || {};
                })()}
                needsComplementaryDoc={option?.needsComplementaryDoc}
                complementaryInstructions={option?.complementaryInstructions}
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createUpload}
                deleteUpload={deleteUpload}
                handleStateOnVerify={handleVerifyState}
                caption={option.points ? `${option.points} points` : ''}
                uploadId={frontUpload?.id}
                rejected={isFileRejected}
                confirmedByCand={frontUpload?.id_upload_detail?.confirmed_by_cand || false}
              />
            ) : (
              <DocumentUpload
                key={option.value}
                uploadCode={uploadCode}
                selfieId={selfieId}
                brand={brand}
                type={option.idType}
                selected={option.selected}
                verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
                skipVerification={option?.skipVerification || false}
                reTakeSelfie={
                  candidateFields?.hiddenValues?.face_similarity_passed === false &&
                  !hasBypassedFaceSimilarity
                }
                documentTitle={option?.name}
                documentDescription={option?.description}
                additionalInstructions={option?.additionalInstructions}
                disabled={option?.disabled}
                countryCode={option?.countryCode}
                expiryTolerance={option?.expiryTolerance}
                value={existing[0]?.id || null}
                listCode={code}
                metadata={{
                  ...(option?.metadata || {}),
                  id_type: option?.value,
                  attempts: existing[0]?.metadata?.attempts || 0,
                }}
                modelParams={modelParams}
                uploadStatus={existing[0]?.id_upload_detail?.status}
                uploadError={existing[0]?.id_upload_detail?.reason_for_failure}
                displayFields={option?.inputFields}
                idValues={(() => {
                  const extracted = existing[0]?.id_upload_detail?.extracted_fields;
                  return extracted || {};
                })()}
                needsComplementaryDoc={option?.needsComplementaryDoc}
                complementaryInstructions={option?.complementaryInstructions}
                getUpload={getUpload}
                signUpload={signUpload}
                createUpload={createUpload}
                deleteUpload={deleteUpload}
                handleStateOnVerify={handleVerifyState}
                caption={option.points ? `${option.points} points` : ''}
                uploadId={existing[0]?.id}
                rejected={isFileRejected}
                confirmedByCand={existing[0]?.id_upload_detail?.confirmed_by_cand || false}
              />
            )
          ) : (
            <MultipleDocumentUpload
              key={option?.value}
              brand={brand}
              idType={option?.value}
              metadata={option?.metadata || {}}
              documentTitle={option?.name}
              documentDescription={option?.description}
              additionalInstructions={option?.additionalInstructions}
              disabled={option?.disabled}
              value={existing || []}
              modelParams={modelParams}
              listCode={code}
              verifyDocument={option?.verifyDocument !== undefined ? option.verifyDocument : true}
              skipVerification={option?.skipVerification || false}
              needsComplementaryDoc={option?.needsComplementaryDoc}
              complementaryInstructions={option?.complementaryInstructions}
              getUpload={getUpload}
              signUpload={signUpload}
              createUpload={createUpload}
              deleteUpload={deleteUpload}
              handleStateOnVerify={handleVerifyState}
              caption={option.points ? `${option.points} points` : ''}
            />
          );
        })}
    </div>
  );
};
